import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time errors anywhere in the React tree and renders a visible
 * fallback. Without this a thrown error during module evaluation or first
 * render leaves the DOM blank, which is hard to debug for end users.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("Unhandled error in DIVEROID account web:", error, info);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="page">
          <main className="page__main">
            <section className="card" role="alert">
              <header className="card__header">
                <h1 className="card__title">Something went wrong</h1>
                <p className="card__subtitle">
                  The page failed to load. Open the browser console for the full stack trace.
                </p>
              </header>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  background: "var(--color-surface-muted)",
                  border: "1px solid var(--color-border)",
                  borderRadius: 10,
                  padding: 12,
                  fontSize: 12,
                  color: "var(--color-text)",
                  overflow: "auto",
                }}
              >
                {this.state.error.message}
              </pre>
            </section>
          </main>
        </div>
      );
    }
    return this.props.children;
  }
}
